/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Search,
  Calendar,
  ChevronDown,
  Minus,
  Plus,
  User,
  Globe,
  Menu,
  Check,
} from "lucide-react";
import { Select, MenuItem, FormControl } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

import logo from "@/assets/Logo.svg";
import {
  useTravelTypes,
  useBudgets,
  useSearchPlaces,
  useCreatePlan,
} from "@/features/planner";
import { RateLimitDialog } from "@/features/planner/components/RateLimitDialog";

const TRIP_MODES = [
  { value: "solo", icon: "👤" },
  { value: "family", icon: "👨‍👩‍👧‍👦" },
  { value: "partner", icon: "💑" },
  { value: "friends", icon: "👥" },
];

export function HeroLanding({ onOpenPlans }: { onOpenPlans?: () => void }) {
  return (
    <section className="bg-white w-full pt-4 md:pt-6 px-4 md:px-8 mb-16">
      <div className="max-w-[1400px] mx-auto relative rounded-[40px] min-h-[750px] shadow-sm bg-slate-100">
        {/* Background Image */}
        <img
          src="/images/nature_travel_hug.png"
          alt="Hero Background"
          className="absolute inset-0 w-full h-full object-cover rounded-[40px] z-0"
        />

        {/* --- CUSTOM OVERLAP HEADER (SINGLE HUMP CUTOUT) --- */}

        {/* Desktop Header Layout */}
        <div className="hidden xl:block">
          {/* Left Tab: Logo (0% to 15%) */}
          <div className="absolute top-0 left-0 w-[15%] h-[80px] bg-white rounded-br-[40px] z-10 flex items-center pl-20">
            <a href="/">
              <img
                src={logo}
                alt="TravelHug"
                className="h-14 w-auto cursor-pointer"
              />
            </a>
          </div>

          {/* Left Inverted Corner */}
          <div
            className="absolute top-0 left-[15%] w-[40px] h-[40px] z-10 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 100% 100%, transparent 40px, white 41px)",
            }}
          />

          {/* Right Inverted Corner */}
          <div
            className="absolute top-0 right-[55%] w-[40px] h-[40px] z-10 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 0% 100%, transparent 40px, white 41px)",
            }}
          />

          {/* Right Tab: Nav (45% to 100%) */}
          <div className="absolute top-0 right-0 w-[55%] h-[80px] bg-white rounded-bl-[40px] z-10 flex items-center justify-end pr-10 gap-8">
            <div className="flex items-center gap-7 text-[16px] font-semibold text-slate-800">
              <a
                href="/"
                className="text-[#ED6F2E] hover:text-[#d65f24] transition-colors"
              >
                Home
              </a>
              <button
                onClick={onOpenPlans}
                className="hover:text-[#0F766E] transition-colors cursor-pointer font-semibold"
              >
                Plan
              </button>
              <a
                href="/#about"
                className="hover:text-[#0F766E] transition-colors"
              >
                About
              </a>
              <a
                href="/#contact"
                className="hover:text-[#0F766E] transition-colors"
              >
                Contact
              </a>
              <div className="flex items-center text-slate-600 hover:text-slate-800 cursor-pointer transition-colors">
                <Globe size={18} className="mr-1.5" /> EN{" "}
                <ChevronDown size={14} className="ml-1 opacity-70" />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile / Tablet Header Layout */}
        <div className="xl:hidden absolute top-0 left-0 w-full h-[90px] bg-white rounded-b-[30px] z-10 flex items-center justify-between px-6 md:px-10 shadow-sm">
          <a href="/">
            <img src={logo} alt="TravelHug" className="h-10 md:h-12 w-auto" />
          </a>
          <div className="flex items-center gap-4">
            <a
              href="/auth/login"
              className="hidden sm:block bg-slate-100 hover:bg-slate-200 text-slate-800 px-5 py-2 rounded-[8px] font-semibold text-sm"
            >
              Log In
            </a>
            <button className="p-2 bg-slate-50 hover:bg-slate-100 rounded-full border border-slate-200">
              <Menu size={24} className="text-slate-800" />
            </button>
          </div>
        </div>

        {/* Login / Sign Up buttons floating on hero (desktop only) */}
        <div className="hidden xl:flex absolute top-[100px] right-10 z-20 items-center gap-3">
          <a
            href="/auth/login"
            className="bg-white border border-gray-200 hover:bg-gray-50 text-slate-800 px-6 py-2.5 rounded-[10px] font-semibold shadow-sm transition-colors text-[15px]"
          >
            Log In
          </a>
          <a
            href="/auth/register"
            className="bg-[#ED6F2E] hover:bg-[#d65f24] text-white px-6 py-2.5 rounded-[10px] font-semibold shadow-sm transition-colors text-[15px]"
          >
            Sign Up
          </a>
        </div>

        {/* Hero Content */}
        <div className="relative z-20 pt-[220px] pb-16 flex flex-col items-center justify-center px-4 w-full h-full pointer-events-none">
          <div className="pointer-events-auto w-full flex flex-col items-center">
            <h1 className="text-white text-[44px] sm:text-5xl md:text-[80px] font-bold mb-4 md:mb-6 text-center tracking-tight drop-shadow-md">
              Start With
            </h1>
            <p className="text-white text-lg sm:text-xl md:text-[28px] font-medium mb-12 text-center drop-shadow-sm max-w-3xl leading-snug">
              2 journeys a month, sign in to{" "}
              <br className="hidden sm:block" /> wander 15 times a day
            </p>

            <LandingPlannerForm />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Functional Planner Form (landing-styled)                          */
/* ------------------------------------------------------------------ */

function LandingPlannerForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // --- form state ---
  const [tripMode, setTripMode] = useState("");
  const [people, setPeople] = useState(4);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [budgetId, setBudgetId] = useState("");
  const [selectedPrefIds, setSelectedPrefIds] = useState<any[]>([]);
  const [query, setQuery] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [placesOpen, setPlacesOpen] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  // validation
  const [errs, setErrs] = useState({
    place: "",
    start: "",
    end: "",
    tripMode: "",
    budget: "",
  });

  // submit / overlays
  const { mutateAsync: createPlanMutate, isPending: submitting } =
    useCreatePlan();
  const [okMsg, setOkMsg] = useState("");
  const [errorOverlay, setErrorOverlay] = useState({
    show: false,
    message: "Something went wrong",
  });
  const [rateLimitDialog, setRateLimitDialog] = useState({
    show: false,
    message: "",
    resetAt: undefined as number | undefined,
  });
  const lastBodyRef = useRef<any>(null);

  // --- API data ---
  const { data: travelTypesData } = useTravelTypes();
  const travelTypes = travelTypesData?.items || [];
  const { data: budgetsData } = useBudgets();
  const budgets = budgetsData?.items || [];

  // --- date helpers ---
  const toLocalYMD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const todayStr = toLocalYMD(new Date());
  const getDayDifference = (s: string, e: string) => {
    const diffTime = new Date(e).getTime() - new Date(s).getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  const endMin = start || todayStr;
  const endMax = start
    ? (() => {
        const d = new Date(start);
        d.setDate(d.getDate() + 10);
        return toLocalYMD(d);
      })()
    : undefined;

  // --- people auto-adjust ---
  useEffect(() => {
    if (tripMode === "solo") setPeople(1);
    else if (tripMode === "partner") setPeople(2);
    else if (people < 1) setPeople(1);
  }, [tripMode]); // eslint-disable-line

  // --- debounced place search ---
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    if (!query || selectedPlace) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query, selectedPlace]);

  const { data: placeSearchResults, error: searchError } = useSearchPlaces(
    debouncedQuery,
    !!debouncedQuery,
  );
  const placeResults = (placeSearchResults || []).slice(0, 10);

  // handle search rate limit
  useEffect(() => {
    if (searchError) {
      const e = searchError as any;
      const status = e?.response?.status;
      const errorData = e?.response?.data?.error;
      if (status === 429 && errorData?.code === "RATE_LIMITED") {
        setRateLimitDialog({
          show: true,
          message: errorData.message || t("planner.rateLimit.searchLimit"),
          resetAt: errorData.details?.reset_at,
        });
        setPlacesOpen(false);
      }
    }
  }, [searchError]); // eslint-disable-line

  // auto-open results
  useEffect(() => {
    if (placeResults.length > 0) setPlacesOpen(true);
  }, [placeResults]); // eslint-disable-line

  // close on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(e.target as Node)
      )
        setPlacesOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // --- validation ---
  const validate = () => {
    const next = { place: "", start: "", end: "", tripMode: "", budget: "" };
    if (!selectedPlace)
      next.place = t("planner.validation.selectDestination");
    if (!start) next.start = t("planner.validation.chooseStartDate");
    else if (start < todayStr)
      next.start = t("planner.validation.startDatePast");
    if (!end) next.end = t("planner.validation.chooseEndDate");
    else if (end < start)
      next.end = t("planner.validation.endDateBeforeStart");
    else if (start && getDayDifference(start, end) > 9)
      next.end = t("planner.validation.maxTenDays", {
        defaultValue: "Trip duration cannot exceed 10 days",
      });
    if (!tripMode) next.tripMode = t("planner.validation.chooseTripType");
    if (!budgetId) next.budget = t("planner.validation.chooseBudget");
    setErrs(next);
    return Object.values(next).every((m) => !m);
  };

  // --- submit ---
  const onSubmit = async () => {
    setOkMsg("");
    if (!validate()) return;
    const budget = budgets.find((b: any) => String(b.id) === String(budgetId));
    const body = {
      travel_type: travelTypes
        .filter((tp: any) => selectedPrefIds.includes(tp.id))
        .map((tp: any) => tp.id)
        .join(","),
      budget_type: (budget?.id || "").toString().toLowerCase(),
      person_count:
        tripMode === "solo" ? 1 : tripMode === "partner" ? 2 : people,
      start_date: start,
      end_date: end,
      destination: selectedPlace?.name || selectedPlace?.destination || "",
      latitude: selectedPlace?.latitude ?? selectedPlace?.lat ?? null,
      longitude: selectedPlace?.longitude ?? selectedPlace?.lng ?? null,
      country_code: selectedPlace?.country_code ?? null,
      language: i18n.resolvedLanguage,
    };
    lastBodyRef.current = body;
    await createPlan(body);
  };

  const createPlan = async (body: any) => {
    try {
      setOkMsg("");
      const response = await createPlanMutate(body);
      setOkMsg(t("planner.messages.planCreated"));
      if (response.slug) {
        navigate({ to: `/plans/$planId`, params: { planId: response.slug } });
      }
    } catch (e: any) {
      setOkMsg("");
      const status = e?.response?.status;
      const errorData = e?.response?.data?.error;
      if (status === 429 && errorData?.code === "RATE_LIMITED") {
        setRateLimitDialog({
          show: true,
          message: errorData.message || t("planner.rateLimit.planLimit"),
          resetAt: errorData.details?.reset_at,
        });
      } else {
        setErrorOverlay({
          show: true,
          message: t("planner.messages.somethingWrong"),
        });
      }
    }
  };

  const handleRetry = async () => {
    setErrorOverlay((s) => ({ ...s, show: false }));
    if (lastBodyRef.current) await createPlan(lastBodyRef.current);
  };

  const stepperDisabled = tripMode === "solo" || tripMode === "partner";

  // --- MUI style overrides for landing design ---
  const landingFieldSx = (hasError?: boolean) => ({
    "& .MuiOutlinedInput-root": {
      height: "64px",
      borderRadius: "16px",
      backgroundColor: "#FFF5EF",
      "& fieldset": {
        borderColor: hasError ? "#f87171" : "transparent",
      },
      "&:hover fieldset": {
        borderColor: hasError ? "#f87171" : "#ED6F2E",
      },
      "&.Mui-focused fieldset": {
        borderColor: hasError ? "#f87171" : "#ED6F2E",
      },
    },
    "& .MuiInputBase-input": {
      color: "#334155",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: 500,
    },
  });

  const landingSelectSx = (hasError?: boolean) => ({
    height: "64px",
    borderRadius: "16px",
    backgroundColor: "#FFF5EF",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: hasError ? "#f87171" : "transparent",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: hasError ? "#f87171" : "#ED6F2E",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: hasError ? "#f87171" : "#ED6F2E",
    },
    color: "#334155",
    fontSize: "15px",
    fontWeight: 500,
  });

  return (
    <>
      {/* Loading overlay */}
      {submitting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl px-8 py-6 text-center shadow-xl">
            <div className="animate-spin w-10 h-10 border-4 border-[#ED6F2E] border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-700 font-medium">
              {t("planner.loading.quoteTop")}
            </p>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {errorOverlay.show && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-[90%] max-w-md px-6 py-6 text-center">
            <h3 className="text-slate-800 font-extrabold text-xl">
              {t("planner.error.title")}
            </h3>
            <p className="mt-2 text-slate-600">{errorOverlay.message}</p>
            <div className="mt-5 flex items-center justify-center gap-3">
              <button
                onClick={handleRetry}
                className="h-11 px-5 rounded-[6px] bg-[#ED6F2E] hover:bg-[#e36522] text-white font-semibold"
              >
                {t("planner.messages.tryAgain")}
              </button>
              <button
                onClick={() =>
                  setErrorOverlay((s) => ({ ...s, show: false }))
                }
                className="h-11 px-4 rounded-[6px] border border-[#E5DED8] text-slate-700 font-semibold bg-white"
              >
                {t("planner.error.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rate limit dialog */}
      <RateLimitDialog
        show={rateLimitDialog.show}
        message={rateLimitDialog.message}
        resetAt={rateLimitDialog.resetAt}
        onLogin={() => {
          window.location.href = "/auth/login";
        }}
        onClose={() => setRateLimitDialog((s) => ({ ...s, show: false }))}
      />

      <form
        className="bg-white p-5 sm:p-6 md:p-8 rounded-[24px] shadow-2xl w-full max-w-[1050px] mt-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        {/* Row 1: Search + Dates */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          {/* Place search */}
          <div className="flex-1 relative" ref={searchBoxRef}>
            <div className="bg-[#FFF5EF] rounded-[16px] flex items-center px-5 h-[64px] transition-colors focus-within:ring-2 focus-within:ring-[#ED6F2E]">
              <Search className="text-gray-400 mr-3" size={22} />
              <input
                value={
                  selectedPlace
                    ? selectedPlace.name || selectedPlace.destination
                    : query
                }
                onChange={(e) => {
                  setSelectedPlace(null);
                  setQuery(e.target.value);
                  if (errs.place) setErrs((s) => ({ ...s, place: "" }));
                }}
                onFocus={() => query && setPlacesOpen(true)}
                placeholder={t("planner.form.searchPlaceholder")}
                className="bg-transparent border-none outline-none w-full text-slate-700 font-medium placeholder:text-gray-400 placeholder:font-normal text-[15px]"
                autoComplete="off"
              />
            </div>
            {placesOpen && placeResults.length > 0 && (
              <ul className="absolute z-30 mt-1 w-full bg-white border border-[#EDE8E3] rounded-[16px] max-h-64 overflow-auto shadow-lg">
                {placeResults.map((p: any, i: number) => (
                  <li
                    key={`${p.id ?? p.name}-${i}`}
                    className="px-4 py-3 hover:bg-[#FFF5EF] cursor-pointer text-slate-700 text-[15px]"
                    onClick={() => {
                      setSelectedPlace(p);
                      setQuery(p.name || p.destination || "");
                      setPlacesOpen(false);
                      if (errs.place) setErrs((s) => ({ ...s, place: "" }));
                    }}
                  >
                    {p.destination}
                  </li>
                ))}
              </ul>
            )}
            {errs.place && (
              <p className="text-red-500 text-xs mt-1 pl-1">{errs.place}</p>
            )}
          </div>

          {/* Start date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="w-full md:w-[240px]">
              <LandingDatePicker
                value={start}
                onChange={(v) => {
                  setStart(v);
                  if (errs.start) setErrs((s) => ({ ...s, start: "" }));
                  if (end && end < v)
                    setErrs((s) => ({
                      ...s,
                      end: t("planner.validation.endDateBeforeStart"),
                    }));
                  else if (end && v && getDayDifference(v, end) > 10)
                    setErrs((s) => ({
                      ...s,
                      end: t("planner.validation.maxTenDays", {
                        defaultValue: "Trip duration cannot exceed 10 days",
                      }),
                    }));
                  else if (errs.end) setErrs((s) => ({ ...s, end: "" }));
                }}
                minDate={todayStr}
                placeholder={t("planner.form.startDate")}
                error={errs.start}
                sx={landingFieldSx(!!errs.start)}
              />
              {errs.start && (
                <p className="text-red-500 text-xs mt-1 pl-1">{errs.start}</p>
              )}
            </div>

            {/* End date */}
            <div className="w-full md:w-[240px]">
              <LandingDatePicker
                value={end}
                onChange={(v) => {
                  setEnd(v);
                  if (start && v && v < start)
                    setErrs((s) => ({
                      ...s,
                      end: t("planner.validation.endDateBeforeStart"),
                    }));
                  else if (start && v && getDayDifference(start, v) > 10)
                    setErrs((s) => ({
                      ...s,
                      end: t("planner.validation.maxTenDays", {
                        defaultValue: "Trip duration cannot exceed 10 days",
                      }),
                    }));
                  else if (errs.end) setErrs((s) => ({ ...s, end: "" }));
                }}
                minDate={endMin}
                maxDate={endMax}
                placeholder={t("planner.form.endDate")}
                error={errs.end}
                sx={landingFieldSx(!!errs.end)}
              />
              {errs.end && (
                <p className="text-red-500 text-xs mt-1 pl-1">{errs.end}</p>
              )}
            </div>
          </LocalizationProvider>
        </div>

        {/* Row 2: Trip Type + People + Budget + Preference */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Trip Type */}
          <div className="flex-1">
            <FormControl fullWidth size="small" error={!!errs.tripMode}>
              <Select
                value={tripMode || ""}
                onChange={(e) => {
                  setTripMode(e.target.value);
                  if (errs.tripMode)
                    setErrs((s) => ({ ...s, tripMode: "" }));
                }}
                displayEmpty
                IconComponent={(props) => (
                  <ChevronDown
                    {...props}
                    size={20}
                    className="text-gray-400 mr-4"
                  />
                )}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span className="text-gray-400 font-medium">
                        {t("planner.form.tripType")}
                      </span>
                    );
                  }
                  const opt = TRIP_MODES.find((m) => m.value === selected);
                  return (
                    <div className="flex items-center gap-2">
                      {opt?.icon && (
                        <span className="text-lg">{opt.icon}</span>
                      )}
                      <span>
                        {t(`planner.tripTypes.${selected}` as any)}
                      </span>
                    </div>
                  );
                }}
                sx={landingSelectSx(!!errs.tripMode)}
              >
                <MenuItem value="" disabled>
                  <span className="text-slate-400">
                    {t("planner.form.tripType")}
                  </span>
                </MenuItem>
                {TRIP_MODES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{m.icon}</span>
                      <span>
                        {t(`planner.tripTypes.${m.value}` as any)}
                      </span>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errs.tripMode && (
              <p className="text-red-500 text-xs mt-1 pl-1">
                {errs.tripMode}
              </p>
            )}
          </div>

          {/* Person stepper */}
          <div className="w-full md:w-[220px]">
            <div
              className={`bg-[#FFF5EF] rounded-[16px] flex items-center justify-between px-4 h-[64px] ${stepperDisabled ? "opacity-60" : ""}`}
            >
              <button
                type="button"
                className="w-8 h-8 rounded-full bg-slate-200/80 hover:bg-slate-300 flex items-center justify-center text-gray-600 transition-colors shadow-sm"
                onClick={() =>
                  !stepperDisabled && setPeople((n) => Math.max(1, n - 1))
                }
                disabled={stepperDisabled}
              >
                <Minus size={16} />
              </button>
              <span className="text-slate-800 font-medium flex items-center gap-2 text-[15px]">
                <User size={18} className="text-gray-400" />
                {tripMode === "solo"
                  ? 1
                  : tripMode === "partner"
                    ? 2
                    : people}{" "}
                {t("planner.form.person")}
              </span>
              <button
                type="button"
                className="w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50 flex items-center justify-center text-gray-600 transition-colors"
                onClick={() => !stepperDisabled && setPeople((n) => n + 1)}
                disabled={stepperDisabled}
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {/* Budget */}
          <div className="flex-1">
            <FormControl fullWidth size="small" error={!!errs.budget}>
              <Select
                value={budgetId || ""}
                onChange={(e) => {
                  setBudgetId(e.target.value);
                  if (errs.budget) setErrs((s) => ({ ...s, budget: "" }));
                }}
                displayEmpty
                IconComponent={(props) => (
                  <ChevronDown
                    {...props}
                    size={20}
                    className="text-gray-400 mr-4"
                  />
                )}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span className="text-gray-400 font-medium">
                        {t("planner.form.budget")}
                      </span>
                    );
                  }
                  const opt = budgets.find(
                    (b: any) => b.id === selected,
                  );
                  return (
                    <div className="flex items-center gap-2">
                      {opt?.icon && (
                        <span className="text-lg">{opt.icon}</span>
                      )}
                      <span>{opt?.label}</span>
                    </div>
                  );
                }}
                sx={landingSelectSx(!!errs.budget)}
              >
                <MenuItem value="" disabled>
                  <span className="text-slate-400">
                    {t("planner.form.budget")}
                  </span>
                </MenuItem>
                {budgets.map((b: any) => (
                  <MenuItem key={b.id} value={b.id}>
                    <div className="flex items-center gap-2">
                      {b.icon && (
                        <span className="text-lg">{b.icon}</span>
                      )}
                      <span>{b.label}</span>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errs.budget && (
              <p className="text-red-500 text-xs mt-1 pl-1">{errs.budget}</p>
            )}
          </div>

          {/* Preference (multi) */}
          <div className="flex-1">
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedPrefIds}
                onChange={(e) => setSelectedPrefIds(e.target.value as any[])}
                displayEmpty
                IconComponent={(props) => (
                  <ChevronDown
                    {...props}
                    size={20}
                    className="text-gray-400 mr-4"
                  />
                )}
                renderValue={(selected: any) => {
                  if (!selected || selected.length === 0) {
                    return (
                      <span className="text-gray-400 font-medium">
                        {t("planner.form.preference")}
                      </span>
                    );
                  }
                  const selectedItems = travelTypes.filter((it: any) =>
                    selected.includes(it.id),
                  );
                  return (
                    <div className="flex items-center gap-1">
                      {selectedItems.slice(0, 2).map((item: any) =>
                        item.icon ? (
                          <span key={item.id} className="text-lg">
                            {item.icon}
                          </span>
                        ) : null,
                      )}
                      <span className="ml-1">
                        {t("planner.form.selectedCount", {
                          count: selectedItems.length,
                        })}
                      </span>
                    </div>
                  );
                }}
                sx={landingSelectSx()}
              >
                {travelTypes.map((it: any) => {
                  const isSelected = selectedPrefIds.includes(it.id);
                  return (
                    <MenuItem key={it.id} value={it.id}>
                      <div className="flex items-center gap-2 w-full">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          className="mr-1"
                        />
                        {it.icon && (
                          <span className="text-lg">{it.icon}</span>
                        )}
                        <span className="flex-1">{it.label}</span>
                        {isSelected && (
                          <Check size={16} className="text-[#0F766E]" />
                        )}
                      </div>
                    </MenuItem>
                  );
                })}
                {travelTypes.length === 0 && (
                  <MenuItem disabled>
                    <span className="text-slate-500">
                      {t("planner.form.noOptions")}
                    </span>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </div>
        </div>

        {/* Row 3: Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full h-[64px] bg-[#ED6F2E] hover:bg-[#d65f24] text-white font-bold text-lg rounded-[16px] transition-colors shadow-md disabled:opacity-70"
        >
          {submitting
            ? t("planner.form.creating")
            : t("planner.form.createButton")}
        </button>

        {okMsg && (
          <div className="text-green-700 text-sm mt-2 text-center">
            {okMsg}
          </div>
        )}
      </form>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Landing-styled DatePicker wrapper                                 */
/* ------------------------------------------------------------------ */
function LandingDatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder,
  error,
  sx,
}: {
  value: string;
  onChange: (v: string) => void;
  minDate?: string;
  maxDate?: string;
  placeholder: string;
  error?: string;
  sx: any;
}) {
  const [open, setOpen] = useState(false);
  const dateValue = value ? dayjs(value) : null;
  const min = minDate ? dayjs(minDate) : dayjs();
  const max = maxDate ? dayjs(maxDate) : undefined;

  return (
    <DatePicker
      value={dateValue}
      onChange={(v: Dayjs | null) => {
        if (v && v.isValid()) onChange(v.format("YYYY-MM-DD"));
        else onChange("");
      }}
      minDate={min}
      maxDate={max}
      format="MMM DD, YYYY"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      slotProps={{
        textField: {
          onClick: () => setOpen(true),
          placeholder,
          error: !!error,
          fullWidth: true,
          size: "small",
          InputProps: {
            startAdornment: (
              <Calendar size={20} className="text-[#ED6F2E] mr-3" />
            ),
          },
          inputProps: {
            readOnly: true,
            style: { cursor: "pointer" },
          },
          sx,
        },
      }}
    />
  );
}
